import React, { useState, useMemo } from 'react';
import { CryptoSelect } from '../atoms/CryptoSelect';
import type { CryptoCurrency } from '../../utils/coingecko';
import type { Payout } from '../../types/payout';
import { normalizeConflict } from '../../utils/cryptoConflicts';

interface CryptoRemapToolProps {
  isOpen: boolean;
  onClose: () => void;
  payouts: Payout[];
  onSave: (updatedPayouts: Payout[]) => Promise<void>;
}

/**
 * Lets the user manually remap a crypto ID across all transactions without
 * requiring a conflict to exist first. Useful when e.g. a CSV import recorded
 * "pearl" but the correct CoinGecko ID is "pearl-2".
 */
export const CryptoRemapTool: React.FC<CryptoRemapToolProps> = ({ isOpen, onClose, payouts, onSave }) => {
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');
  const [targetCrypto, setTargetCrypto] = useState<CryptoCurrency | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Build unique list of all cryptos currently in payouts
  const uniqueCryptos = useMemo(() => {
    const map = new Map<string, { id: string; symbol: string; name: string }>();
    for (const p of payouts) {
      if (p.crypto) map.set(p.crypto.id, p.crypto);
      if (p.sentCrypto) map.set(p.sentCrypto.id, p.sentCrypto);
      if (p.receivedCrypto) map.set(p.receivedCrypto.id, p.receivedCrypto);
    }
    return Array.from(map.values()).sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [payouts]);

  const handleClose = () => {
    onClose();
    setSelectedSourceId('');
    setTargetCrypto(null);
    setSuccessMsg('');
  };

  const handleApply = async () => {
    if (!selectedSourceId || !targetCrypto) return;
    if (selectedSourceId === targetCrypto.id) return;

    setIsSaving(true);
    try {
      const updated = normalizeConflict(payouts, selectedSourceId, targetCrypto);
      await onSave(updated);
      const count = payouts.filter(p =>
        p.crypto?.id === selectedSourceId ||
        p.sentCrypto?.id === selectedSourceId ||
        p.receivedCrypto?.id === selectedSourceId
      ).length;
      setSuccessMsg(`${count} transaction${count > 1 ? 's' : ''} corrigée${count > 1 ? 's' : ''} : ${selectedSourceId} → ${targetCrypto.id}`);
      setSelectedSourceId('');
      setTargetCrypto(null);
      setTimeout(() => setSuccessMsg(''), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const sourceCrypto = uniqueCryptos.find(c => c.id === selectedSourceId);
  const canApply = selectedSourceId && targetCrypto && selectedSourceId !== targetCrypto.id;

  return (
    <>
      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

          {/* Dialog */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Modifier un coinID</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Remplace l'ID CoinGecko d'une crypto sur toutes vos transactions.
                  Utile si un import CSV a enregistré le mauvais ID (ex :{' '}
                  <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">pearl</code> au lieu de{' '}
                  <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">pearl-2</code>).
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg transition-colors ml-4 shrink-0"
              >
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Source */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Crypto à corriger (ID actuel)
                  </label>
                  <select
                    value={selectedSourceId}
                    onChange={e => { setSelectedSourceId(e.target.value); setTargetCrypto(null); }}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— Sélectionner —</option>
                    {uniqueCryptos.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.symbol.toUpperCase()} — {c.name} ({c.id})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Remplacer par (chercher sur CoinGecko)
                  </label>
                  <CryptoSelect
                    value={targetCrypto}
                    onChange={setTargetCrypto}
                    placeholder="Rechercher le bon ID…"
                  />
                </div>
              </div>

              {/* Preview */}
              {sourceCrypto && targetCrypto && selectedSourceId !== targetCrypto.id && (
                <div className="text-xs text-blue-700 bg-blue-50 rounded px-3 py-2">
                  <code className="bg-blue-100 px-1 rounded">{selectedSourceId}</code>
                  {' '}({sourceCrypto.name}) →{' '}
                  <code className="bg-blue-100 px-1 rounded">{targetCrypto.id}</code>
                  {' '}({targetCrypto.name}) sur{' '}
                  {payouts.filter(p =>
                    p.crypto?.id === selectedSourceId ||
                    p.sentCrypto?.id === selectedSourceId ||
                    p.receivedCrypto?.id === selectedSourceId
                  ).length} transaction(s)
                </div>
              )}

              {sourceCrypto && targetCrypto && selectedSourceId === targetCrypto.id && (
                <div className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">
                  L'ID CoinGecko trouvé (<code className="bg-amber-100 px-1 rounded">{targetCrypto.id}</code>)
                  {' '}est déjà celui utilisé sur vos transactions : il n'y a rien à corriger ici.
                  Si les prix ne se chargent toujours pas pour ce token, le problème vient probablement
                  d'ailleurs (LiveCoinWatch, cache de prix) — pas d'un mauvais ID.
                </div>
              )}

              {successMsg && (
                <div className="text-xs text-green-700 bg-green-50 rounded px-3 py-2">
                  ✓ {successMsg}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:dark:bg-gray-900 transition-colors"
              >
                Fermer
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={!canApply || isSaving}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? 'Application…' : 'Appliquer la correction'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
